import { useId } from 'react';
import { text } from '../../app/i18n';
import type { CustomEditor } from '../editor/editor-types';
import type { Catalogue, CatalogueField, View } from './views-model';

/**
 * Éditeurs dédiés du profil « vue » : choix d'un champ dans le catalogue servi (avec ce que la politique et
 * les index en disent), tons d'une énumération, champs de la recherche, filtre enregistré (champ, opérateur,
 * valeur), regroupement, préréglages de période. Aucun ne décide d'un accès : le serveur relit tout.
 */

const OPERATORS: ReadonlyArray<[string, string]> = [
  ['eq', 'est'],
  ['ne', 'n’est pas'],
  ['in', 'parmi'],
  ['gt', 'après / plus que'],
  ['gte', 'à partir de'],
  ['lt', 'avant / moins que'],
  ['lte', 'jusqu’à'],
  ['contains', 'contient'],
  ['starts_with', 'commence par'],
  ['exists', 'est renseigné'],
];
const TONES: ReadonlyArray<[string, string]> = [
  ['', '—'],
  ['ok', 'Vert (ok)'],
  ['info', 'Bleu (info)'],
  ['warn', 'Orange (attention)'],
  ['danger', 'Rouge (danger)'],
  ['neutral', 'Gris (neutre)'],
];
export const PRESET_LABELS: Readonly<Record<string, string>> = {
  all: 'Tout',
  today: 'Aujourd’hui',
  yesterday: 'Hier',
  last_7_days: '7 derniers jours',
  last_30_days: '30 derniers jours',
  this_week: 'Cette semaine',
  this_month: 'Ce mois',
  last_month: 'Mois dernier',
  this_quarter: 'Ce trimestre',
  this_year: 'Cette année',
  last_year: 'Année dernière',
};

const isRecord = (value: unknown): value is Record<string, unknown> => value !== null && typeof value === 'object' && !Array.isArray(value);
const labelOf = (field: CatalogueField): string => text(field.label as Record<string, string> | undefined, 'fr', field.field_id);

/** Ce qu'un champ appelle comme mise en garde, en clair, à côté de son nom. */
export function fieldNote(field: CatalogueField): string {
  const notes: string[] = [];
  if (field.readable_by.length === 0) notes.push('lu par aucun rôle');
  else if (field.blocked_for.length > 0) notes.push(`non lu par ${field.blocked_for.join(', ')}`);
  if (field.origin === 'customer') notes.push('champ du client');
  if (field.indexed === false && field.filterable) notes.push('non indexé');
  return notes.join(' · ');
}

function FieldSelect({ label, value, fields, onChange, required = true }: { label: string; value: unknown; fields: readonly CatalogueField[]; onChange: (value: unknown) => void; required?: boolean }) {
  const id = useId();
  const current = typeof value === 'string' ? value : '';
  const known = fields.some((field) => field.field_id === current);
  const chosen = fields.find((field) => field.field_id === current);
  return (
    <div className="ay-props__row">
      <label htmlFor={id} className="ay-field__label">{label}{required && <span aria-hidden="true"> *</span>}</label>
      <select id={id} value={current} onChange={(event) => onChange(event.target.value === '' ? undefined : event.target.value)}>
        {(!required || current === '') && <option value="">—</option>}
        {!known && current !== '' && <option value={current}>{current} (inconnu)</option>}
        {fields.map((field) => (
          <option key={field.field_id} value={field.field_id}>
            {labelOf(field)}{fieldNote(field) === '' ? '' : ` — ${fieldNote(field)}`}
          </option>
        ))}
      </select>
      {chosen !== undefined && fieldNote(chosen) !== '' && <span className="ay-props__help">{fieldNote(chosen)}</span>}
    </div>
  );
}

/** Champ d'une colonne, d'un filtre, d'un tri : un champ du catalogue qui satisfait `accept`. */
export function fieldEditor(catalogue: Catalogue, accept: (field: CatalogueField) => boolean): CustomEditor<View> {
  return ({ label, value, onChange }) => <FieldSelect label={label} value={value} fields={catalogue.filter(accept)} onChange={(next) => onChange(next)} />;
}

/** Tons des valeurs d'une colonne d'énumération : une pastille par valeur, le libellé toujours affiché. */
export function tonesEditor(catalogue: Catalogue, fieldOf: (pointer: string) => string | undefined): CustomEditor<View> {
  return function TonesEditor({ label, value, onChange, pointer }) {
    const id = useId();
    const field = catalogue.find((entry) => entry.field_id === fieldOf(pointer));
    const values = field?.enum_values ?? [];
    if (values.length === 0) return <div className="ay-props__row"><span className="ay-field__label">{label}</span><span className="ay-text ay-text--muted">Pour une liste de valeurs seulement.</span></div>;
    const tones = isRecord(value) ? (value as Record<string, string>) : {};
    const set = (entry: string, tone: string): void => {
      const next = { ...tones, [entry]: tone };
      if (tone === '') delete next[entry];
      onChange(Object.keys(next).length === 0 ? undefined : next);
    };
    return (
      <fieldset className="ay-props__group">
        <legend className="ay-field__label">{label}</legend>
        {values.map((entry) => (
          <span key={entry} className="ay-props__list-row">
            <label htmlFor={`${id}-${entry}`} className="ay-views__value">{entry}</label>
            <select id={`${id}-${entry}`} value={tones[entry] ?? ''} onChange={(event) => set(entry, event.target.value)}>
              {TONES.map(([tone, name]) => <option key={tone} value={tone}>{name}</option>)}
            </select>
          </span>
        ))}
      </fieldset>
    );
  };
}

/** Champs de la recherche : des cases à cocher sur les champs texte cherchables (huit au plus). */
export function searchFieldsEditor(catalogue: Catalogue): CustomEditor<View> {
  return function SearchFields({ label, value, onChange }) {
    const chosen = Array.isArray(value) ? value.map(String) : [];
    const searchable = catalogue.filter((field) => field.searchable);
    const toggle = (fieldId: string, on: boolean): void => {
      const next = on ? [...chosen, fieldId] : chosen.filter((entry) => entry !== fieldId);
      onChange(next.length === 0 ? undefined : next);
    };
    return (
      <fieldset className="ay-props__group">
        <legend className="ay-field__label">{label}</legend>
        {searchable.length === 0 && <span className="ay-text ay-text--muted">Aucun champ texte cherchable sur cet objet.</span>}
        {searchable.map((field) => (
          <label key={field.field_id} className="ay-props__row ay-props__row--inline">
            <input type="checkbox" checked={chosen.includes(field.field_id)} disabled={!chosen.includes(field.field_id) && chosen.length >= 8} onChange={(event) => toggle(field.field_id, event.target.checked)} />
            <span>{labelOf(field)}</span>
          </label>
        ))}
        {chosen.filter((fieldId) => !searchable.some((field) => field.field_id === fieldId)).map((fieldId) => (
          <span key={fieldId} className="ay-field__error">« {fieldId} » n’est pas cherchable : <button type="button" className="ay-editor__tool" onClick={() => toggle(fieldId, false)}>retirer</button></span>
        ))}
      </fieldset>
    );
  };
}

type Clause = { field: string; operator: string; value: string };

function clausesOf(filter: unknown): Clause[] {
  if (!isRecord(filter)) return [];
  return Object.entries(filter).flatMap(([field, operators]) => (isRecord(operators) ? Object.entries(operators).map(([operator, value]) => ({ field, operator, value: Array.isArray(value) ? value.join(', ') : String(value ?? '') })) : []));
}

function filterOf(clauses: readonly Clause[]): Record<string, Record<string, string | string[]>> {
  const out: Record<string, Record<string, string | string[]>> = {};
  for (const clause of clauses) {
    if (clause.field === '') continue;
    const value = clause.operator === 'in' ? clause.value.split(',').map((entry) => entry.trim()).filter((entry) => entry !== '') : clause.operator === 'exists' ? (clause.value === 'false' ? 'false' : 'true') : clause.value;
    out[clause.field] = { ...(out[clause.field] ?? {}), [clause.operator]: value };
  }
  return out;
}

/** Filtre enregistré : des conditions champ / opérateur / valeur, la forme gouvernée de l'Object API. */
export function quickFilterEditor(catalogue: Catalogue): CustomEditor<View> {
  return function QuickFilter({ label, value, onChange }) {
    const id = useId();
    const clauses = clausesOf(value);
    const filterable = catalogue.filter((field) => field.filterable || field.envelope === true);
    const set = (next: Clause[]): void => onChange(filterOf(next));
    return (
      <fieldset className="ay-props__group" aria-label={label}>
        <legend className="ay-field__label">{label}</legend>
        {clauses.length === 0 && <span className="ay-text ay-text--muted">Aucune condition : le filtre montre tout.</span>}
        {clauses.map((clause, index) => {
          const field = catalogue.find((entry) => entry.field_id === clause.field);
          return (
            <span key={index} className="ay-views__clause">
              <select aria-label={`Champ de la condition ${index + 1}`} value={clause.field} onChange={(event) => set(clauses.map((entry, position) => (position === index ? { ...entry, field: event.target.value } : entry)))}>
                {filterable.map((entry) => <option key={entry.field_id} value={entry.field_id}>{labelOf(entry)}</option>)}
              </select>
              <select aria-label={`Opérateur de la condition ${index + 1}`} value={clause.operator} onChange={(event) => set(clauses.map((entry, position) => (position === index ? { ...entry, operator: event.target.value } : entry)))}>
                {OPERATORS.map(([operator, name]) => <option key={operator} value={operator}>{name}</option>)}
              </select>
              {field?.enum_values !== undefined && clause.operator !== 'in' && clause.operator !== 'exists' ? (
                <select aria-label={`Valeur de la condition ${index + 1}`} value={clause.value} onChange={(event) => set(clauses.map((entry, position) => (position === index ? { ...entry, value: event.target.value } : entry)))}>
                  {field.enum_values.map((entry) => <option key={entry} value={entry}>{entry}</option>)}
                </select>
              ) : (
                <input id={`${id}-${index}`} aria-label={`Valeur de la condition ${index + 1}`} type="text" value={clause.value} placeholder={clause.operator === 'in' ? 'valeur, valeur' : ''} onChange={(event) => set(clauses.map((entry, position) => (position === index ? { ...entry, value: event.target.value } : entry)))} />
              )}
              <button type="button" className="ay-editor__tool" aria-label={`Retirer la condition ${index + 1}`} onClick={() => set(clauses.filter((_, position) => position !== index))}>×</button>
            </span>
          );
        })}
        <button type="button" className="ay-button ay-button--secondary" disabled={filterable.length === 0} onClick={() => set([...clauses, { field: filterable[0]!.field_id, operator: 'eq', value: filterable[0]!.enum_values?.[0] ?? '' }])}>+ Ajouter une condition</button>
      </fieldset>
    );
  };
}

/** Regroupement : un ou deux champs filtrables, lus ensemble par la lecture « groupes ». */
export function groupEditor(catalogue: Catalogue): CustomEditor<View> {
  return function Group({ label, value, onChange }) {
    const chosen = Array.isArray(value) ? value.map(String) : typeof value === 'string' ? [value] : [];
    const groupable = catalogue.filter((field) => field.filterable || field.field_id === 'entity_id');
    const set = (first: string | undefined, second: string | undefined): void => {
      const next = [first, second].filter((entry): entry is string => entry !== undefined && entry !== '');
      onChange(next.length === 0 ? undefined : next.length === 1 ? next[0] : next);
    };
    return (
      <fieldset className="ay-props__group">
        <legend className="ay-field__label">{label}</legend>
        <FieldSelect label="Regrouper par" value={chosen[0]} fields={groupable} required={false} onChange={(next) => set(next as string | undefined, chosen[1])} />
        {chosen[0] !== undefined && <FieldSelect label="Puis par" value={chosen[1]} fields={groupable.filter((field) => field.field_id !== chosen[0])} required={false} onChange={(next) => set(chosen[0], next as string | undefined)} />}
      </fieldset>
    );
  };
}

/** Préréglages d'une période : des cases à cocher, dans l'ordre du contrat. */
export const presetsEditor: CustomEditor<View> = ({ label, value, onChange }) => {
  const chosen = Array.isArray(value) ? value.map(String) : [];
  return (
    <fieldset className="ay-props__group">
      <legend className="ay-field__label">{label} *</legend>
      {Object.entries(PRESET_LABELS).map(([preset, name]) => (
        <label key={preset} className="ay-props__row ay-props__row--inline">
          <input type="checkbox" checked={chosen.includes(preset)} onChange={(event) => {
            const next = Object.keys(PRESET_LABELS).filter((entry) => (entry === preset ? event.target.checked : chosen.includes(entry)));
            onChange(next.length === 0 ? undefined : next);
          }} />
          <span>{name}</span>
        </label>
      ))}
    </fieldset>
  );
};
